import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface Credits {
  questions: number;
  synastry: number;
  astromatch: number;
  quiromancy: number;
}

const UNLIMITED: Credits = { questions: Infinity, synastry: Infinity, astromatch: Infinity, quiromancy: Infinity };

export function useCredits() {
  const { user, profile } = useAuth();
  const [credits, setCredits] = useState<Credits>({ questions: 0, synastry: 0, astromatch: 0, quiromancy: 0 });
  const [loading, setLoading] = useState(true);
  const [creditsError, setCreditsError] = useState(false);

  const isUnlimited = profile?.unlimited_access === true || profile?.role === 'admin';

  const fetchCredits = useCallback(async () => {
    if (!user) { setLoading(false); return; }

    // Unlimited-access and admin users never consume credits, no DB fetch needed.
    if (isUnlimited) {
      setCredits(UNLIMITED);
      setCreditsError(false);
      setLoading(false);
      return;
    }

    const [q, s, a] = await Promise.all([
      supabase.from('user_question_credits').select('balance').eq('user_id', user.id).maybeSingle(),
      supabase.from('user_synastry_credits').select('balance').eq('user_id', user.id).maybeSingle(),
      supabase.from('astromatch_credits').select('balance').eq('user_id', user.id).maybeSingle(),
    ]);

    // If any query returned a hard error, expose it so consumers can show a
    // retry UI instead of treating 0 as "free access".
    if (q.error || s.error || a.error) {
      setCreditsError(true);
      setLoading(false);
      return;
    }

    setCreditsError(false);
    setCredits({
      questions:  q.data?.balance ?? 0,
      synastry:   s.data?.balance ?? 0,
      astromatch: a.data?.balance ?? 0,
      quiromancy: 0,
    });
    setLoading(false);
  }, [user?.id, isUnlimited]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  return { credits, isUnlimited, loading, creditsError, refresh: fetchCredits };
}
